/**
 * sessionTimeline.js — Circular event buffer.
 *
 * Holds up to MAX_EVENTS events in insertion order (newest last).
 * All memory is in-process only — no localStorage, no serialization yet.
 *
 * Future: serialize to sessionStorage for tab-refresh continuity,
 * then to the Supabase session_events table when the backend layer is ready.
 */

const MAX_EVENTS = 120;

let _timeline = [];

/* ─── Deduplication ──────────────────────────────────────────────────── */

/**
 * Returns true if the last event is effectively identical to the incoming
 * one (same type + module + entityId within 500ms). Prevents React strict-mode
 * double-fires and rapid sequential re-pushes of the same route change.
 */
function isDuplicate(event) {
  if (_timeline.length === 0) return false;
  const last = _timeline[_timeline.length - 1];
  return (
    last.type     === event.type   &&
    last.module   === event.module &&
    last.entityId === event.entityId &&
    event.ts - last.ts < 500
  );
}

/* ─── Public API ─────────────────────────────────────────────────────── */

/**
 * Appends an event to the timeline unless it's a duplicate.
 * Trims to MAX_EVENTS when the buffer overflows.
 *
 * @returns {boolean} true if the event was added, false if deduplicated
 */
export function pushToTimeline(event) {
  if (isDuplicate(event)) return false;

  _timeline.push(event);

  if (_timeline.length > MAX_EVENTS) {
    _timeline = _timeline.slice(-MAX_EVENTS);
  }

  return true;
}

/**
 * Returns a shallow copy of the current timeline (newest last).
 * @returns {RuntimeEvent[]}
 */
export function getTimeline() {
  return _timeline.slice();
}

/**
 * Clears all events from the timeline.
 * Use for explicit session resets or during automated testing.
 */
export function clearTimeline() {
  _timeline = [];
}

/**
 * Returns the most recent N events without copying the full timeline.
 * @param {number} n
 * @returns {RuntimeEvent[]}
 */
export function getRecentFromTimeline(n) {
  return _timeline.slice(-n);
}
